import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, signal, TemplateRef, viewChild, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { PanModalComponent } from '../../../../components/pan-modal/pan-modal.component';
import { hasStatus, tryGetErrorMessage } from '../../../../error.utils';
import { copyToClipboard } from '../../../../public/util/navigator.utils';
import { AssignmentService } from '../../../../service/assignment.service';
import { LossPreventionService } from '../../../../service/loss-prevention.service';
import { NotifyService } from '../../../../service/notify.service';
import { PosthogService } from '../../../../service/posthog.service';
import { UserLoginService } from '../../../../service/user-login.service';
import {
  AssignmentDetails,
  FeatureFlag,
  IncidentFormContext,
  IncidentFormGroup,
  ReviewStatus,
  ReviewSubjectType,
  SubmitReviewRequest,
} from '../../../../types';
import { ReviewSubmissionFailedModalComponent } from '../review-submission-failed-modal/review-submission-failed-modal.component';
import { ReviewFormService } from '../review-submit-form/review-form.service';
import { getLpReviewUrl, parseReviewSubjectType, reviewOutcomeToFormValues } from '../review-utils';
import { AssignmentDetailsStore } from './assignment-details.store';

// LP sends every other review type back to Core
const LP_REVIEW_SUBJECTS: ReviewSubjectType[] = [ReviewSubjectType.Cancellation, ReviewSubjectType.Walkout, ReviewSubjectType.Deepdive];

@Component({
  selector: 'app-investigation-assignment',
  templateUrl: './investigation-assignment.component.html',
  styleUrl: './investigation-assignment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [AssignmentDetailsStore],
})
export class InvestigationAssignmentComponent implements OnDestroy, OnInit {
  public reviewFormService = inject(ReviewFormService);
  private assignmentService = inject(AssignmentService);
  private lpService = inject(LossPreventionService);
  private assignmentDetailsStore = inject(AssignmentDetailsStore);
  private notifyService = inject(NotifyService);
  private userService = inject(UserLoginService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private posthogService = inject(PosthogService);

  public assignmentId = signal<string | undefined>(undefined);
  public assignmentIdShort = computed(() => this.assignmentId()?.split('-')[0] ?? '');
  public incidentType = signal<ReviewSubjectType | undefined>(undefined);
  public readonly ReviewSubjectType = ReviewSubjectType;
  public assignment = signal<AssignmentDetails | undefined>(undefined);

  public locationId = signal<number | undefined>(undefined);
  public instanceAtDate = signal<Date | undefined>(undefined);
  public form = signal<IncidentFormGroup | undefined>(undefined);
  public formTemplate: TemplateRef<unknown> | null = null;
  public assignmentError = signal<string | undefined>(undefined);

  public isLoading = signal<boolean>(false);
  public isCompleted = signal<boolean>(false);
  public isSubmitting = signal<boolean>(false);
  public errorMessage = signal<string | undefined>(undefined);
  public submitFailureError = signal<string | undefined>(undefined);
  public allowAdditionalSubmissions = signal<boolean>(false);
  public isCreatingAssignment = signal<boolean>(false);
  private submittedModal = viewChild<PanModalComponent>('submittedModal');
  private failedModal = viewChild<ReviewSubmissionFailedModalComponent>('failedModal');

  public isExpired = computed(() => {
    const a = this.assignment();
    if (!a) return false;
    const expiresAt = a.expires_at as unknown;
    const ms = expiresAt instanceof Date ? expiresAt.getTime() : new Date(expiresAt as string).getTime();
    return isFinite(ms) && ms - Date.now() < 0;
  });

  public lpReviewUrl = computed(() => {
    if (!this.posthogService.isFeatureEnabledByEnum(FeatureFlag.EnableLpCameraLinks)) return undefined;
    const assignmentId = this.assignmentId();
    const incidentType = this.incidentType();
    if (!assignmentId || !incidentType || !LP_REVIEW_SUBJECTS.includes(incidentType)) return undefined;
    return getLpReviewUrl(assignmentId);
  });

  public ngOnInit() {
    this.assignmentId.set(this.route.snapshot.params.assignmentId);
    this.loadAssignment();
  }

  public ngOnDestroy() {
    this.reviewFormService.resetForm();
  }

  public onContextReady(ctx: IncidentFormContext) {
    this.locationId.set(ctx.locationId);
    this.instanceAtDate.set(ctx.instanceAtDate);
    this.form.set(ctx.form);
  }

  public onContextError(error: unknown) {
    this.assignmentError.set(tryGetErrorMessage(error));
  }

  public title(): string {
    const t = this.incidentType();
    if (t === ReviewSubjectType.Cancellation) return 'Cancellation';
    if (t === ReviewSubjectType.Underringing) return 'Underringing';
    if (t === ReviewSubjectType.Sideloaded) return 'Sideloaded';
    if (t === ReviewSubjectType.Walkout) return 'Walkout';
    return 'Assignment';
  }

  public submit() {
    const review = this.form().value;
    const findings = this.reviewFormService.findingsForm.value;

    this.isSubmitting.set(true);
    this.reviewFormService.uploadFiles().subscribe({
      next: ({ uploadedPhotosLinks, uploadedReportsLinks }) => {
        const submitRequest: SubmitReviewRequest = {
          is_theft: review.isTheft,
          case_id: review.matchingCase === 'new_case' ? null : review.matchingCase,
          report_links: uploadedReportsLinks,
          photos_links: uploadedPhotosLinks,
          extra_links: review.extraLinks.map((l) => ({ link: l })),
          theft_type: {
            is_cancellation: review.theftType.is_cancellation ?? false,
            is_underringing: review.theftType.is_underringing ?? false,
            is_walkout: review.theftType.is_walkout ?? false,
            is_cashier: review.theftType.is_cashier ?? false,
          },
          suspected_items: review.suspectedItems?.map((i) => ({ id: i.id!, total: i.total })),
          comments: review.isTheft ? findings.comments : this.reviewFormService.getNtfReason(),
          additional_stolen_value: review.additionalStolenValue,
          instance_at: this.reviewFormService.getValidInstanceAt(review.instance_at, this.instanceAtDate()),
          recent_walk_in_ids: review.selectedImages,
          use_kiosk_photo: review.useKioskPhoto,
        };

        this.lpService
          .submitReview(this.assignmentId(), submitRequest)
          .pipe(finalize(() => this.isSubmitting.set(false)))
          .subscribe({
            next: () => {
              this.lpService
                .getQueueByReviewId(this.assignmentId())
                .pipe(finalize(() => this.submittedModal()?.show()))
                .subscribe({
                  next: (queue) => this.allowAdditionalSubmissions.set(queue?.additional_submissions === true),
                  error: () => this.allowAdditionalSubmissions.set(false),
                });
            },
            error: (err) => {
              const errorRequest = {
                comments: JSON.stringify(err),
                error_message: err.error?.error ?? '',
                errors: [{ is_blocking: false, review_error_type: 'Exception' }],
              };
              this.lpService.submitError(this.assignmentId(), errorRequest).subscribe(
                () => {},
                () => {},
              );
              this.submitFailureError.set(tryGetErrorMessage(err));
              this.failedModal()?.open();
            },
          });
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.notifyService.notify(`Failed to upload images: ${tryGetErrorMessage(err)}`);
      },
    });
  }

  public goToDashboard() {
    const isInvestigator = this.userService.currentProfileRoleList().includes('investigator');
    this.router.navigate([isInvestigator ? '/lp/review' : '/lp']);
  }

  public createAdditionalAssignment() {
    if (this.isCreatingAssignment()) return;
    this.isCreatingAssignment.set(true);
    this.assignmentService
      .createAssignment(this.locationId(), 'Additional submission', ReviewSubjectType.Sideloaded, false)
      .pipe(finalize(() => this.isCreatingAssignment.set(false)))
      .subscribe({
        next: (assignment) => (window.location.href = `/lp/review/${assignment.review_id}`),
        error: (err) => this.notifyService.notify(tryGetErrorMessage(err)),
      });
  }

  public copyReviewId() {
    copyToClipboard(this.assignmentId());
    this.notifyService.notify('Copied to clipboard!');
  }

  private loadAssignment() {
    this.assignmentService
      .getAssignment(this.assignmentId())
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        (assignment) => {
          this.assignment.set(assignment);
          this.assignmentDetailsStore.set(assignment);
          this.incidentType.set(parseReviewSubjectType(assignment.subject_type));
          // load existing submission so users can VIEW (not edit)
          if (assignment.status.toLowerCase() === ReviewStatus.Completed || assignment.status.toLowerCase() === ReviewStatus.Validated) {
            this.isCompleted.set(true);
            this.isLoading.set(true);
            this.assignmentService
              .getReviewOutcomes(this.assignmentId())
              .pipe(finalize(() => this.isLoading.set(false)))
              .subscribe((outcomes) => {
                if (!outcomes || outcomes.length === 0) return;
                const last = outcomes[outcomes.length - 1];
                const v = reviewOutcomeToFormValues(last);
                this.reviewFormService.applyInitialValues(v);
              });
          }
        },
        (err) => {
          if (hasStatus(err) && err.status === 404) {
            this.errorMessage.set('Assignment was not found (it may have been archived)');
          }
        },
      );
  }
}
